/**
 * AnomaliesPage — `/anomalies`. Система помощи в принятии решений: не только
 * выявляем аномалию, но по кнопке даём от нейросети КОНКРЕТНОЕ действие +
 * ожидаемый эффект (прирост заявок / снижение CPL) и кнопку «Применить в чате».
 */

import { useState } from 'react';
import { usePageHeader } from '@/components/layout/pageHeader';
import { Badge, Button, Icon, Empty, Spinner, LoadingBlock, ErrorBlock } from '@/shared/ui';
import { apiFetch } from '@/api/client';
import { useAiStore } from '@/state/aiStore';
import { useAiChat } from '@/hooks/api/useAiChat';
import { useAnomalies, type Anomaly, type AnomalySeverity } from '@/hooks/api/useReports';

const SEVERITY: Record<AnomalySeverity, { tone: string; label: string; bar: string }> = {
  high: { tone: 'b-danger', label: 'критично', bar: 'var(--danger)' },
  medium: { tone: 'b-warning', label: 'внимание', bar: 'var(--warning)' },
  low: { tone: 'b-info', label: 'инфо', bar: 'var(--info)' },
};

interface Recommendation {
  action: string;
  impact: string;
  kind: string;
}

export function AnomaliesPage() {
  usePageHeader({ title: 'Аномалии', subtitle: 'Помощь в принятии решений — что делать и что это даст' });

  const { data, isLoading, isError, error, refetch } = useAnomalies();

  if (isLoading) return <LoadingBlock label="Проверяю аномалии…" />;
  if (isError) return <ErrorBlock text={(error as Error)?.message} onRetry={() => void refetch()} />;
  if (!data || !data.anomalies.length) {
    return <Empty icon="CheckCircle" title="Аномалий нет" text="Расход и цели в норме." />;
  }

  return (
    <div className="col gap12" style={{ maxWidth: 900 }}>
      {data.anomalies.map((a) => (
        <AnomalyCard key={a.id} a={a} />
      ))}
    </div>
  );
}

function AnomalyCard({ a }: { a: Anomaly }) {
  const sev = SEVERITY[a.severity];
  const [rec, setRec] = useState<Recommendation | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const openPanel = useAiStore((s) => s.openPanel);
  const { sendMessage } = useAiChat();

  const loadRec = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await apiFetch<Recommendation>('/api/ai/recommend', {
        method: 'POST',
        json: {
          title: a.title,
          detail: a.detail,
          campaign: a.campaign,
          campaignId: a.campaignId,
          domain: a.domain,
          severity: a.severity,
        },
      });
      setRec(r);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    openPanel();
    void sendMessage(
      `По аномалии «${a.title}»${a.campaign ? ` (кампания ${a.campaign})` : ''}: ${a.detail}\n` +
        (rec ? `Предлагаемое действие: ${rec.action}\n` : '') +
        `Подготовь и обоснуй это действие. Любое изменение боевого аккаунта — только после моего апрува.`,
    );
  };

  return (
    <div className="card card--sev col" style={{ gap: 10, ['--sev' as string]: sev.bar }}>
      <div className="row" style={{ gap: 14, alignItems: 'flex-start' }}>
        <div className="col grow" style={{ gap: 4 }}>
          <div className="row center gap8">
            <Badge tone={sev.tone}>{sev.label}</Badge>
            <span style={{ fontWeight: 700 }}>{a.title}</span>
          </div>
          <span className="muted" style={{ fontSize: 13.5 }}>{a.detail}</span>
          {a.campaign && (
            <span className="mono muted" style={{ fontSize: 11.5 }}>кампания: {a.campaign}</span>
          )}
        </div>
        {!rec && (
          <Button variant="ai" size="sm" icon="Sparkles" disabled={loading} onClick={() => void loadRec()}>
            {loading ? 'Думаю…' : 'Что делать?'}
          </Button>
        )}
      </div>

      {loading && !rec && (
        <div className="row center gap8 muted" style={{ fontSize: 13 }}>
          <Spinner /> Нейросеть анализирует и считает эффект…
        </div>
      )}
      {err && <span style={{ color: 'var(--danger-fg)', fontSize: 13 }}>{err}</span>}

      {rec && (
        <div
          className="col"
          style={{ gap: 8, padding: '12px 14px', borderRadius: 'var(--r-md)', background: 'var(--surface-alt)', border: '1px solid var(--border)' }}
        >
          <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
            <Icon name="Settings" className="ic" style={{ marginTop: 2, color: 'var(--text-muted)' }} />
            <div><b>Что делать:</b> {rec.action}</div>
          </div>
          {rec.impact && (
            <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
              <Icon name="Activity" className="ic" style={{ marginTop: 2, color: 'var(--text-muted)' }} />
              <div><b>Эффект:</b> {rec.impact}</div>
            </div>
          )}
          <div className="row" style={{ gap: 8, marginTop: 2 }}>
            <Button variant="ai" size="sm" icon="ArrowRight" onClick={apply}>Применить в чате</Button>
            <Button variant="text" size="sm" icon="Sparkles" onClick={() => void loadRec()}>Другой вариант</Button>
          </div>
        </div>
      )}
    </div>
  );
}
